import React, { useContext, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { FiPlus } from "react-icons/fi";
import { useMutation } from "react-query";
import {
  addAssessmentApi,
  removeAssessmentApi,
  updateAssessmentApi,
} from "../../../../api/appointments";
import { GlobalContext } from "../../../../context/Provider";
import FormSelect from "../../../../components/form/FromSelect";
import {
  assessmentData,
  planData,
  signsAndSymptomsData,
} from "../../../../utils/continuationSheetData";

const emptyAssessment = {
  signsAndSymptoms: "",
  examination: [],
  assessment: "",
  plan: "",
  diagnoses: [],
};

const DoctorAssessments = ({ appointment, canEdit, refetch }) => {
  const { authState } = useContext(GlobalContext);
  const toast = useToast();
  const editor = useDisclosure();
  const [selected, setSelected] = useState(null);
  const [values, setValues] = useState(emptyAssessment);
  const [exam, setExam] = useState({ exam: "", note: "" });
  const [diagnosis, setDiagnosis] = useState({
    name: "",
    verificationStatus: "provisional",
    note: "",
  });

  useEffect(() => {
    if (selected) {
      setValues({
        signsAndSymptoms: selected.signsAndSymptoms || "",
        examination: selected.examination || [],
        assessment: selected.assessment || "",
        plan: selected.plan || "",
        diagnoses: selected.diagnoses || [],
      });
    } else setValues(emptyAssessment);
  }, [selected]);

  const close = () => {
    editor.onClose();
    setSelected(null);
    setExam({ exam: "", note: "" });
    setDiagnosis({ name: "", verificationStatus: "provisional", note: "" });
  };
  const success = async (title) => {
    await refetch();
    close();
    toast({ title, status: "success", duration: 3000, isClosable: true });
  };
  const failure = (error) =>
    toast({
      title: "Unable to save assessment",
      description: error.response?.data?.msg || "Please try again.",
      status: "error",
      duration: 4000,
      isClosable: true,
    });
  const saveMutation = useMutation(
    selected ? updateAssessmentApi : addAssessmentApi,
    {
      onSuccess: () =>
        success(selected ? "Assessment updated" : "Assessment added"),
      onError: failure,
    },
  );
  const removeMutation = useMutation(removeAssessmentApi, {
    onSuccess: async () => {
      await refetch();
      toast({
        title: "Assessment removed",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: failure,
  });

  const addExam = () => {
    if (!exam.exam.trim()) return;
    setValues({
      ...values,
      examination: [
        ...values.examination,
        { exam: exam.exam.trim(), note: exam.note.trim() },
      ],
    });
    setExam({ exam: "", note: "" });
  };
  const addDiagnosis = () => {
    if (!diagnosis.name.trim()) return;
    setValues({
      ...values,
      diagnoses: [
        ...values.diagnoses,
        { ...diagnosis, name: diagnosis.name.trim() },
      ],
    });
    setDiagnosis({ name: "", verificationStatus: "provisional", note: "" });
  };
  const isAuthor = (item) =>
    String(item.createdBy?._id || item.createdBy) ===
    String(authState.user?._id);

  return (
    <Box maxW="1050px" mx="auto">
      <Flex
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap="4"
        mb="5"
      >
        <Box>
          <Heading size="md">Doctor assessments</Heading>
          <Text mt="1" color="gray.500" fontSize="sm">
            Clinical assessments recorded during this attendance.
          </Text>
        </Box>
        {canEdit && (
          <Button
            size="sm"
            leftIcon={<FiPlus />}
            onClick={() => {
              setSelected(null);
              editor.onOpen();
            }}
          >
            New assessment
          </Button>
        )}
      </Flex>

      {!appointment?.assessments?.length ? (
        <Box
          bg="white"
          borderWidth="1px"
          borderStyle="dashed"
          borderRadius="xl"
          py="12"
          textAlign="center"
        >
          <Heading size="sm">No assessment recorded</Heading>
          <Text mt="2" color="gray.500" fontSize="sm">
            Add the doctor's findings, assessment and plan for this attendance.
          </Text>
        </Box>
      ) : (
        <Stack spacing="4">
          {[...appointment.assessments].reverse().map((item, index) => (
            <Box
              key={item._id}
              bg="white"
              borderWidth="1px"
              borderRadius="2xl"
              overflow="hidden"
            >
              <Flex p="4" justify="space-between" align="flex-start" gap="4">
                <Box>
                  <HStack>
                    <Heading size="sm">
                      Assessment {appointment.assessments.length - index}
                    </Heading>
                    {item.legacySource && <Badge>Imported</Badge>}
                  </HStack>
                  <Text mt="1" color="gray.500" fontSize="xs">
                    {item.createdAt
                      ? new Date(item.createdAt).toLocaleString()
                      : "Date unavailable"}
                    {item.createdBy?.firstName
                      ? ` · ${item.createdBy.firstName} ${item.createdBy.lastName}`
                      : ""}
                  </Text>
                </Box>
                {canEdit && isAuthor(item) && !item.legacySource && (
                  <HStack>
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<EditIcon />}
                      onClick={() => {
                        setSelected(item);
                        editor.onOpen();
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      leftIcon={<DeleteIcon />}
                      isLoading={removeMutation.isLoading}
                      onClick={() =>
                        removeMutation.mutate({
                          appointmentId: appointment._id,
                          assessmentId: item._id,
                        })
                      }
                    >
                      Remove
                    </Button>
                  </HStack>
                )}
              </Flex>
              <Divider />
              <SimpleGrid columns={{ base: 1, md: 3 }} gap="4" p="5">
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="800"
                    color="gray.500"
                    textTransform="uppercase"
                  >
                    Signs and symptoms
                  </Text>
                  <Text mt="2" whiteSpace="pre-wrap">
                    {item.signsAndSymptoms || "Not recorded"}
                  </Text>
                </Box>
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="800"
                    color="gray.500"
                    textTransform="uppercase"
                  >
                    Assessment
                  </Text>
                  <Text mt="2" whiteSpace="pre-wrap">
                    {item.assessment || "Not recorded"}
                  </Text>
                </Box>
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="800"
                    color="gray.500"
                    textTransform="uppercase"
                  >
                    Plan
                  </Text>
                  <Text mt="2" whiteSpace="pre-wrap">
                    {item.plan || "Not recorded"}
                  </Text>
                </Box>
              </SimpleGrid>
              {!!item.examination?.length && (
                <Box px="5" pb="5">
                  <Text
                    fontSize="xs"
                    fontWeight="800"
                    color="gray.500"
                    textTransform="uppercase"
                  >
                    Examination
                  </Text>
                  <SimpleGrid mt="2" columns={{ base: 1, md: 2 }} gap="2">
                    {item.examination.map((entry) => (
                      <Box
                        key={entry._id || `${entry.exam}-${entry.note}`}
                        bg="gray.50"
                        borderRadius="lg"
                        p="3"
                      >
                        <Text fontWeight="700" fontSize="sm">
                          {entry.exam}
                        </Text>
                        {entry.note && (
                          <Text mt="1" fontSize="sm">
                            {entry.note}
                          </Text>
                        )}
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              )}
              {!!item.diagnoses?.length && (
                <Flex px="5" pb="5" gap="2" flexWrap="wrap">
                  {item.diagnoses.map((entry) => (
                    <Badge
                      key={entry._id || entry.name}
                      colorScheme={
                        entry.verificationStatus === "confirmed"
                          ? "green"
                          : "yellow"
                      }
                      borderRadius="full"
                      px="3"
                      py="1"
                    >
                      {entry.name} · {entry.verificationStatus}
                    </Badge>
                  ))}
                </Flex>
              )}
            </Box>
          ))}
        </Stack>
      )}

      <Modal
        isOpen={editor.isOpen}
        onClose={close}
        size="3xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent borderRadius="2xl">
          <ModalHeader>
            {selected ? "Edit doctor assessment" : "New doctor assessment"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing="5">
              <Box>
                <Heading size="sm">Examination findings</Heading>
                <Stack mt="3" spacing="2">
                  {values.examination.map((item, index) => (
                    <Flex
                      key={`${item.exam}-${index}`}
                      bg="gray.50"
                      p="3"
                      borderRadius="lg"
                      justify="space-between"
                    >
                      <Text fontSize="sm">
                        <b>{item.exam}</b>
                        {item.note ? ` — ${item.note}` : ""}
                      </Text>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() =>
                          setValues({
                            ...values,
                            examination: values.examination.filter(
                              (_, itemIndex) => itemIndex !== index,
                            ),
                          })
                        }
                      >
                        Remove
                      </Button>
                    </Flex>
                  ))}
                </Stack>
                <SimpleGrid mt="3" columns={{ base: 1, md: 2 }} gap="3">
                  <Input
                    value={exam.exam}
                    onChange={(event) =>
                      setExam({ ...exam, exam: event.target.value })
                    }
                    placeholder="Examination name"
                  />
                  <Input
                    value={exam.note}
                    onChange={(event) =>
                      setExam({ ...exam, note: event.target.value })
                    }
                    placeholder="Finding or note"
                  />
                </SimpleGrid>
                <Button mt="2" size="sm" variant="outline" onClick={addExam}>
                  Add examination
                </Button>
              </Box>
              <Divider />
              <SimpleGrid columns={{ base: 1, md: 3 }} gap="4">
                <FormControl>
                  <FormLabel>Signs and symptoms</FormLabel>
                  <FormSelect
                    onChange={(event) =>
                      setValues({
                        ...values,
                        signsAndSymptoms: `${event.target.value}, ${values.signsAndSymptoms}`,
                      })
                    }
                    options={signsAndSymptomsData}
                    placeholder="Signs and Symptoms"
                    mb="2"
                  />
                  <Textarea
                    placeholder="Type here..."
                    rows="6"
                    value={values.signsAndSymptoms}
                    onChange={(event) =>
                      setValues({
                        ...values,
                        signsAndSymptoms: event.target.value,
                      })
                    }
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Assessment</FormLabel>
                  <FormSelect
                    onChange={(event) =>
                      setValues({
                        ...values,
                        assessment: `${event.target.value}, ${values.assessment}`,
                      })
                    }
                    options={assessmentData}
                    placeholder="Assessment"
                    mb="2"
                  />
                  <Textarea
                    placeholder="Type here..."
                    rows="6"
                    value={values.assessment}
                    onChange={(event) =>
                      setValues({ ...values, assessment: event.target.value })
                    }
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Plan</FormLabel>
                  <FormSelect
                    onChange={(event) =>
                      setValues({
                        ...values,
                        plan: `${event.target.value}, ${values.plan}`,
                      })
                    }
                    options={planData}
                    placeholder="Plan"
                    mb="2"
                  />
                  <Textarea
                    placeholder="Type here..."
                    rows="6"
                    value={values.plan}
                    onChange={(event) =>
                      setValues({ ...values, plan: event.target.value })
                    }
                  />
                </FormControl>
              </SimpleGrid>

              {/* <Divider />
              <Box>
                <Heading size="sm">Diagnoses</Heading>
                <SimpleGrid mt="3" columns={{ base: 1, md: 2 }} gap="3">
                  <Input
                    value={diagnosis.name}
                    onChange={(event) =>
                      setDiagnosis({ ...diagnosis, name: event.target.value })
                    }
                    placeholder="Diagnosis"
                  />
                  <Select
                    value={diagnosis.verificationStatus}
                    onChange={(event) =>
                      setDiagnosis({
                        ...diagnosis,
                        verificationStatus: event.target.value,
                      })
                    }
                  >
                    <option value="provisional">Provisional</option>
                    <option value="differential">Differential</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="refuted">Refuted</option>
                  </Select>
                </SimpleGrid>
                <Button
                  mt="2"
                  size="sm"
                  variant="outline"
                  onClick={addDiagnosis}
                >
                  Add diagnosis
                </Button>
                <Flex mt="3" gap="2" flexWrap="wrap">
                  {values.diagnoses.map((item, index) => (
                    <Badge
                      key={`${item.name}-${index}`}
                      borderRadius="full"
                      px="3"
                      py="1"
                      cursor="pointer"
                      onClick={() =>
                        setValues({
                          ...values,
                          diagnoses: values.diagnoses.filter(
                            (_, itemIndex) => itemIndex !== index,
                          ),
                        })
                      }
                    >
                      {item.name} · {item.verificationStatus} ×
                    </Badge>
                  ))}
                </Flex>
              </Box> */}
            </Stack>
          </ModalBody>
          <ModalFooter gap="3">
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              isLoading={saveMutation.isLoading}
              onClick={() =>
                saveMutation.mutate({
                  appointmentId: appointment._id,
                  ...(selected ? { assessmentId: selected._id } : {}),
                  data: values,
                })
              }
            >
              Save assessment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DoctorAssessments;
